import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BrandShell } from '../components/brand-shell.js';
import { LegalLinks } from '../components/legal-links.js';
import { listPlans, type PublicPlan } from '../lib/signup-api.js';
import {
  clearSignupDraft,
  EMPTY_DRAFT,
  loadSignupDraft,
  saveSignupDraft,
  type SignupDraft,
} from '../lib/signup-draft.js';
import {
  loadPendingSignup,
  markPendingSignupProvisioning,
  readRedirectStatus,
  savePendingSignup,
  stripRedirectParams,
  type PendingSignup,
} from '../lib/signup-session.js';

import { PaymentStep } from './payment-step.js';
import { PlanPicker } from './plan-picker.js';
import { ProvisioningStatus } from './provisioning-status.js';
import { SignupForm } from './signup-form.js';

type Step = 'pick' | 'form' | 'pay' | 'status';

const STEP_HEADINGS: Record<Step, string> = {
  pick: 'Create your community',
  form: 'Tell us about your community',
  pay: 'Payment details',
  status: 'Almost there',
};

interface ResumeState {
  pending: PendingSignup;
  step: Step;
  notice: string | null;
}

/**
 * Stripe redirect-based payment methods return to this page with
 * `redirect_status` in the query string; restore the persisted signup so the
 * customer lands on the right step instead of step 1 (and re-signs up).
 *
 * Also resumes on a plain reload once payment was handed off (the persisted
 * `phase`), because the return params are gone by then. The only write is the
 * idempotent phase marker; it must not strip the URL. React may run a state initializer more
 * than once (StrictMode) or remount the tree after a render error; if the first
 * run removed the params, the second would see none and drop the customer on
 * the plan picker.
 */
function readResume(): ResumeState | null {
  const outcome = readRedirectStatus(window.location.search);
  const pending = loadPendingSignup();
  if (!pending) return null;
  if (!outcome) {
    // Reload after the return params were stripped: the persisted phase says the
    // customer already paid, so keep resolving the server-side signup status.
    return pending.phase === 'status' ? { pending, step: 'status', notice: null } : null;
  }
  if (outcome === 'failed') {
    return { pending, step: 'pay', notice: 'Your payment was not completed. Please try again.' };
  }
  markPendingSignupProvisioning();
  return { pending, step: 'status', notice: null };
}

/**
 * Resolved once per page load and kept for the life of the module, so a
 * remount after the return params were stripped still resumes.
 */
let resumeCache: { value: ResumeState | null } | null = null;

function getResume(): ResumeState | null {
  if (!resumeCache) resumeCache = { value: readResume() };
  return resumeCache.value;
}

/** Test seam: forget the per-page-load resume snapshot. */
export function resetResumeCache(): void {
  resumeCache = null;
}

function isFormHistory(state: unknown): boolean {
  return (
    typeof state === 'object' && state !== null && 'gfStep' in state && state.gfStep === 'form'
  );
}

/** Own only the pre-payment steps; Stripe redirect resume keeps its own lifecycle. */
function useStepHistory(
  step: Step,
  hasPlan: boolean,
  resuming: boolean,
  setStep: (step: Step) => void,
): { enterForm: () => void; backToPlans: () => void } {
  const goingBack = useRef(false);

  useEffect(() => {
    if (!resuming) window.history.replaceState({ gfStep: 'pick' }, '');
  }, [resuming]);

  useEffect(() => {
    if (step !== 'pick' && step !== 'form') return;
    const onPopState = (event: PopStateEvent): void => {
      goingBack.current = false;
      setStep(isFormHistory(event.state) && hasPlan ? 'form' : 'pick');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [step, hasPlan, setStep]);

  return {
    enterForm: (): void => {
      if (isFormHistory(window.history.state)) {
        window.history.replaceState({ gfStep: 'form' }, '');
      } else {
        window.history.pushState({ gfStep: 'form' }, '');
      }
      setStep('form');
    },
    backToPlans: (): void => {
      if (goingBack.current) return;
      if (isFormHistory(window.history.state)) {
        goingBack.current = true;
        window.history.back();
      } else {
        setStep('pick');
      }
    },
  };
}

export function SignupFlow() {
  const [resume] = useState(getResume);

  useEffect(() => {
    if (resume) stripRedirectParams();
  }, [resume]);
  const [step, setStep] = useState<Step>(resume?.step ?? 'pick');
  const [planId, setPlanId] = useState(resume?.pending.plan.id ?? '');
  const [selectedPlan, setSelectedPlan] = useState<PublicPlan | null>(resume?.pending.plan ?? null);
  const [brandedSubdomain, setBrandedSubdomain] = useState(
    resume?.pending.plan.brandedSubdomain ?? false,
  );
  const [clientSecret, setClientSecret] = useState(resume?.pending.clientSecret ?? '');
  const [intentType, setIntentType] = useState<'setup' | 'payment'>(
    resume?.pending.intentType ?? 'payment',
  );
  const [signupId, setSignupId] = useState(resume?.pending.signupId ?? '');
  const [draft, setDraft] = useState(loadSignupDraft);
  const onDraftChange = useCallback((next: SignupDraft): void => {
    setDraft(next);
    saveSignupDraft(next);
  }, []);
  const { enterForm, backToPlans } = useStepHistory(
    step,
    selectedPlan !== null,
    resume !== null,
    setStep,
  );

  const {
    data: plans,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: listPlans,
  });

  return (
    <BrandShell>
      <h1 className="text-lg font-semibold">{STEP_HEADINGS[step]}</h1>

      {step === 'pick' && (
        <div className="mt-4">
          {isLoading && <p className="text-center text-gray-500 py-12">Loading plans…</p>}
          {isError && (
            <p className="text-center text-red-500 py-12">
              Failed to load plans. Please refresh the page.
            </p>
          )}
          {plans && (
            <PlanPicker
              plans={plans}
              onSelect={(plan) => {
                setPlanId(plan.id);
                setSelectedPlan(plan);
                setBrandedSubdomain(plan.brandedSubdomain);
                enterForm();
              }}
            />
          )}
        </div>
      )}

      {step === 'form' && (
        <div className="mt-4">
          <button
            type="button"
            onClick={backToPlans}
            className="-ml-3 mb-1 inline-flex min-h-11 items-center rounded-md px-3 text-sm font-medium text-brand hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            ← Back to plans
          </button>
          <SignupForm
            planId={planId}
            plan={selectedPlan}
            initialDraft={draft}
            onDraftChange={onDraftChange}
            brandedSubdomain={brandedSubdomain}
            onCreated={({ signupId: sid, clientSecret: cs, intentType: it }) => {
              clearSignupDraft();
              setDraft(EMPTY_DRAFT);
              if (selectedPlan) {
                savePendingSignup({
                  signupId: sid,
                  clientSecret: cs,
                  intentType: it,
                  plan: selectedPlan,
                });
              }
              setSignupId(sid);
              setClientSecret(cs);
              setIntentType(it);
              setStep('pay');
            }}
          />
        </div>
      )}

      {step === 'pay' && selectedPlan && (
        <div className="mt-4">
          {resume?.notice && (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {resume.notice}
            </p>
          )}
          <PaymentStep
            clientSecret={clientSecret}
            intentType={intentType}
            plan={selectedPlan}
            onComplete={() => {
              markPendingSignupProvisioning();
              setStep('status');
            }}
          />
        </div>
      )}

      {step === 'status' && <ProvisioningStatus signupId={signupId} />}

      {step !== 'status' && <LegalLinks />}

      <p className="mt-5 border-t pt-4 text-center text-sm text-gray-500">
        Already using GroupFire?{' '}
        <a href="https://app.groupfire.com" className="font-medium text-brand underline">
          Sign in →
        </a>
      </p>
    </BrandShell>
  );
}
